<template>
  <div class="radar-promo">
    <aside class="radar-promo__info">
      <header class="radar-promo__header">
        <div class="radar-promo__logo">
          <LogoSVG />
        </div>
        <h1 class="radar-promo__title">SHERIFF Радар</h1>
      </header>
      <div class="radar-promo__body">
        <p class="radar-promo__lead">Працює у містах:</p>
        <div v-if="cities.length" class="radar-promo__search">
          <svg class="radar-promo__search-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5L21 21" />
          </svg>
          <input
            v-model="query"
            class="radar-promo__search-input"
            type="search"
            placeholder="Знайти місто"
            aria-label="Знайти місто"
            autocomplete="off"
            @keydown.enter.prevent="focusFirstMatch"
          />
        </div>
        <ul v-if="filteredCities.length" class="radar-promo__cities">
          <li v-for="city in filteredCities" :key="city.name">
            <button
              type="button"
              class="radar-promo__city"
              :class="{ 'radar-promo__city--active': activeCity === city.name }"
              @click="focusCity(city)"
            >
              {{ city.name }}
            </button>
          </li>
        </ul>
        <p v-else-if="cities.length" class="radar-promo__not-found">
          У місті «{{ query.trim() }}» Радар поки не працює
        </p>
        <p v-else-if="error" class="radar-promo__error">
          Не вдалося завантажити карту міст, спробуйте пізніше
        </p>
        <YellowButton
          @clickBtn="$router.push('/login')"
          class="radius radar-promo__button"
        >
          Увійти в особистий кабінет
        </YellowButton>
      </div>
    </aside>
    <div class="radar-promo__map-wrap">
      <div ref="map" class="radar-promo__map"></div>
      <p v-if="mapUnavailable" class="radar-promo__map-unavailable">
        Карта недоступна у цьому браузері
      </p>
      <Spinner v-if="loading" class="radar-promo__spinner" />
    </div>
  </div>
</template>

<script>
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import YellowButton from "@/components/Buttons/YellowButton.vue";
import LogoSVG from "@/components/SVG/Logo.vue";
import Spinner from "@/components/Spinner";
// Кордон України з OSM (relation 60199, разом з Кримом), спрощений до ~2 км.
import UKRAINE_BORDER from "./ukraine-border.json";

// Центр України: карта стоїть тут, поки міста не завантажились. MapLibre: [lon, lat].
const UKRAINE_CENTER = [31.2, 48.6];

// Брейкпоінт той самий, що й у стилях.
const MOBILE_QUERY = "(max-width: 768px)";

const PLACE_NAME = ["coalesce", ["get", "name:uk"], ["get", "name"]];
// У містах тайлів iso_a2 порожній, тож країну визначає геометрія.
const IN_UKRAINE = ["within", UKRAINE_BORDER];

// Векторні тайли OpenFreeMap без ключа. Зі стилю лишено тільки населені пункти
// України, ледь помітні море та державний кордон; ліси, рельєф, дороги, будівлі — ні.
const MAP_STYLE = {
  version: 8,
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
  sources: {
    openmaptiles: {
      type: "vector",
      url: "https://tiles.openfreemap.org/planet",
      attribution:
        '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> ' +
        '<a href="https://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> ' +
        '<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap</a>',
    },
  },
  layers: [
    { id: "background", type: "background", paint: { "background-color": "#131313" } },
    {
      id: "sea",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "water",
      filter: ["==", ["get", "class"], "ocean"],
      paint: { "fill-color": "#0d0d0d" },
    },
    {
      id: "country-border",
      type: "line",
      source: "openmaptiles",
      "source-layer": "boundary",
      filter: ["all", ["==", ["get", "admin_level"], 2], ["!=", ["get", "maritime"], 1], ["!=", ["get", "disputed"], 1]],
      paint: { "line-color": "#313131", "line-width": 1 },
    },
    {
      id: "village",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      minzoom: 9,
      filter: ["all", IN_UKRAINE, ["==", ["get", "class"], "village"]],
      layout: {
        "text-field": PLACE_NAME,
        "text-font": ["Noto Sans Regular"],
        "text-size": 11,
        "symbol-sort-key": ["get", "rank"],
        // Назва під точкою: над нею стоїть пин Радару.
        "text-anchor": "top",
        "text-offset": [0, 0.3],
      },
      paint: { "text-color": "#6e6e6e", "text-halo-color": "#131313", "text-halo-width": 1.5 },
    },
    {
      id: "town",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      minzoom: 6.5,
      filter: ["all", IN_UKRAINE, ["==", ["get", "class"], "town"]],
      layout: {
        "text-field": PLACE_NAME,
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 7, 11, 11, 14],
        "symbol-sort-key": ["get", "rank"],
        // Назва під точкою: над нею стоїть пин Радару.
        "text-anchor": "top",
        "text-offset": [0, 0.3],
      },
      paint: { "text-color": "#8a8a8a", "text-halo-color": "#131313", "text-halo-width": 1.5 },
    },
    {
      id: "city",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      filter: ["all", IN_UKRAINE, ["==", ["get", "class"], "city"]],
      layout: {
        "text-field": PLACE_NAME,
        "text-font": ["Noto Sans Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, 8, 15, 11, 18],
        "symbol-sort-key": ["get", "rank"],
        // Назва під точкою: над нею стоїть пин Радару.
        "text-anchor": "top",
        "text-offset": [0, 0.3],
      },
      paint: { "text-color": "#d9d9d9", "text-halo-color": "#131313", "text-halo-width": 1.5 },
    },
  ],
};

// Шари підписів підкладки: з них прибираються міста Радару, бо в них свій підпис під пином.
const PLACE_LAYERS = ["village", "town", "city"];

// Пин — повернутий квадрат, тож вістря на size/2 + size/√2 від верху; назва міста під ним.
function pinElement(size, name) {
  const element = document.createElement("div");
  const label = document.createElement("span");

  element.className = size < 24 ? "radar-pin radar-pin--small" : "radar-pin";
  element.style.height = `${Math.round(size / 2 + size / Math.SQRT2)}px`;
  element.setAttribute("title", name);
  label.className = "radar-pin__label";
  label.textContent = name;
  element.appendChild(label);

  return element;
}

// Пошук не зважає на регістр і різні апострофи («Кам'янське», «Камʼянське»).
function normalize(text) {
  return text.toLowerCase().replace(/['’ʼ`]/g, "").trim();
}

export default {
  components: {
    YellowButton,
    LogoSVG,
    Spinner,
  },
  data() {
    return {
      cities: [],
      query: "",
      activeCity: null,
      loading: true,
      error: false,
      mapUnavailable: false,
    };
  },
  computed: {
    filteredCities() {
      const query = normalize(this.query);

      return query ? this.cities.filter((city) => normalize(city.name).includes(query)) : this.cities;
    },
  },
  mounted() {
    this.isMobile = window.matchMedia(MOBILE_QUERY).matches;
    // Карта й пини не реактивні, тому не в data.
    this.markers = {};

    try {
      this.map = new maplibregl.Map({
        container: this.$refs.map,
        style: MAP_STYLE,
        center: UKRAINE_CENTER,
        zoom: this.isMobile ? 4.5 : 5,
        minZoom: 4,
        maxZoom: 13,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        // Сторінку гортає один палець і звичайне колесо; карту — два пальці або Ctrl/⌘ + колесо.
        cooperativeGestures: true,
        attributionControl: { compact: true },
        locale: {
          "CooperativeGesturesHandler.WindowsHelpText": "Ctrl + колесо миші, щоб змінити масштаб",
          "CooperativeGesturesHandler.MacHelpText": "⌘ + колесо миші, щоб змінити масштаб",
          "CooperativeGesturesHandler.MobileHelpText": "Двома пальцями, щоб рухати карту",
          "NavigationControl.ZoomIn": "Наблизити",
          "NavigationControl.ZoomOut": "Віддалити",
        },
      });
      this.map.touchZoomRotate.disableRotation();
      this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
      if (this.isMobile) {
        // Компактні атрибуції стартують розгорнутими і на телефоні закривають низ карти;
        // згортаємо до кнопки «i», текст лишається доступним по тапу.
        this.map.once("load", () => {
          const attribution = this.$refs.map.querySelector(".maplibregl-ctrl-attrib");
          if (attribution) {
            attribution.classList.remove("maplibregl-compact-show");
          }
        });
      }
    } catch (e) {
      // Без WebGL MapLibre не стартує; список міст і пошук лишаються робочими.
      this.mapUnavailable = true;
    }

    this.loadCities();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    loadCities() {
      // Без таймауту завислий запит лишає лоадер назавжди.
      this.axios
        .get(`${this.$domain}/air-radar/cities`, { timeout: 15000 })
        .then(({ data }) => {
          this.cities = data.data || [];
          this.drawPins();
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    drawPins() {
      if (!this.map || !this.cities.length) {
        return;
      }

      const size = this.isMobile ? 20 : 24;
      const bounds = new maplibregl.LngLatBounds();

      this.cities.forEach((city) => {
        const point = [city.lon, city.lat];
        const element = pinElement(size, city.name);

        element.addEventListener("click", () => this.focusCity(city));
        this.markers[city.name] = new maplibregl.Marker({ element, anchor: "bottom" }).setLngLat(point).addTo(this.map);
        bounds.extend(point);
      });

      this.hideBasemapLabels(this.cities.map((city) => city.name));
      this.map.on("moveend", () => this.declutterLabels());

      // Зверху запас під висоту пина, знизу й з боків — під назву.
      this.map.fitBounds(bounds, {
        padding: this.isMobile
          ? { top: 32, bottom: 28, left: 40, right: 40 }
          : { top: 56, bottom: 48, left: 72, right: 72 },
        maxZoom: 9,
        animate: false,
      });
      this.declutterLabels();
    },
    // HTML-підписи пинів не беруть участі в колізіях карти: близькі міста
    // (Миколаїв і Херсон) на малому масштабі налазять. Ховаємо ті, що перетинають
    // уже показані; першим іде вибране місто, далі порядок з API.
    declutterLabels() {
      const placed = [];
      const ordered = [...this.cities].sort(
        (a, b) => (b.name === this.activeCity) - (a.name === this.activeCity)
      );

      ordered.forEach((city) => {
        const label = this.markers[city.name].getElement().firstChild;
        label.style.visibility = "visible";
        const box = label.getBoundingClientRect();
        const overlaps = placed.some(
          (other) => box.left < other.right && box.right > other.left && box.top < other.bottom && box.bottom > other.top
        );

        label.style.visibility = overlaps ? "hidden" : "visible";
        if (!overlaps) {
          placed.push(box);
        }
      });
    },
    hideBasemapLabels(names) {
      const apply = () => {
        PLACE_LAYERS.forEach((layer) => {
          this.map.setFilter(layer, [
            "all",
            this.map.getFilter(layer),
            ["!", ["in", PLACE_NAME, ["literal", names]]],
          ]);
        });
      };

      // Фільтри можна міняти лише після завантаження стилю.
      if (this.map.isStyleLoaded()) {
        apply();
      } else {
        this.map.once("load", apply);
      }
    },
    focusCity(city) {
      if (this.activeCity && this.markers[this.activeCity]) {
        this.markers[this.activeCity].getElement().classList.remove("radar-pin--active");
      }
      this.activeCity = city.name;

      if (!this.map) {
        return;
      }

      this.markers[city.name].getElement().classList.add("radar-pin--active");
      this.map.flyTo({ center: [city.lon, city.lat], zoom: 10 });

      // На телефоні список під картою: без прокрутки зум відбувся б поза екраном.
      if (this.isMobile) {
        this.$refs.map.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    },
    focusFirstMatch(event) {
      if (!this.filteredCities.length) {
        return;
      }

      // Ховає клавіатуру на телефоні, щоб було видно карту.
      event.target.blur();
      this.focusCity(this.filteredCities[0]);
    },
  },
};
</script>

<style scoped lang="scss">
.radar-promo {
  display: flex;
  min-height: 100vh;
  background-color: #131313;
  &__info {
    flex: 0 0 400px;
    box-sizing: border-box;
    padding: 56px 40px;
    color: #fff;
  }
  &__logo {
    margin-bottom: 56px;
  }
  &__title {
    margin: 0 0 16px;
    color: #fce500;
    font-size: 40px;
    font-weight: 700;
    line-height: normal;
  }
  &__lead {
    margin: 0 0 24px;
    font-size: 18px;
    line-height: 150%;
  }
  &__search {
    position: relative;
    margin-bottom: 16px;
  }
  &__search-icon {
    position: absolute;
    top: 50%;
    left: 14px;
    width: 20px;
    height: 20px;
    transform: translateY(-50%);
    fill: none;
    stroke: #8a8a8a;
    stroke-width: 2;
    stroke-linecap: round;
    pointer-events: none;
  }
  &__search-input {
    width: 100%;
    height: 48px;
    box-sizing: border-box;
    padding: 0 16px 0 44px;
    border: 1px solid #313131;
    border-radius: 6px;
    background: #1c1c1c;
    color: #fff;
    font: inherit;
    // Менше 16px — і iOS зумить сторінку при фокусі.
    font-size: 16px;
    outline: none;
    -webkit-appearance: none;
    &::placeholder {
      color: #8a8a8a;
    }
    &:focus {
      border-color: #fce500;
    }
  }
  &__cities {
    list-style: none;
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    // Глобальний ul > li дає 24px відступу.
    & > li {
      margin: 0;
    }
  }
  &__city {
    padding: 8px 16px;
    border: 1px solid #fce500;
    border-radius: 20px;
    background: transparent;
    color: #fff;
    font: inherit;
    font-size: 14px;
    cursor: pointer;
    &:hover,
    &--active {
      background: #fce500;
      color: #131313;
    }
  }
  &__not-found {
    margin: 0 0 40px;
    color: #8a8a8a;
    font-size: 14px;
    line-height: 150%;
  }
  &__error {
    margin: 0 0 40px;
    color: #ff6b6b;
    font-size: 14px;
  }
  &__button {
    width: 100%;
    max-width: 330px;
    height: 58px;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0.32px;
  }
  &__map-wrap {
    flex: 1;
    position: relative;
    min-height: 100vh;
  }
  &__map {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  &__map-unavailable {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    margin: 0;
    color: #8a8a8a;
    text-align: center;
  }
  &__spinner {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
    background: rgba(19, 19, 19, 0.6);
  }
}

@media (max-width: 1024px) {
  .radar-promo {
    &__info {
      flex-basis: 320px;
      padding: 40px 24px;
    }
    &__logo {
      margin-bottom: 40px;
    }
    &__title {
      font-size: 32px;
    }
    &__button {
      font-size: 14px;
    }
  }
}

@media (max-width: 768px) {
  .radar-promo {
    flex-direction: column;
    // Шапка, карта, список: aside розпадається на дві частини навколо карти.
    &__info {
      display: contents;
    }
    &__header {
      order: 0;
      padding: 20px 16px 16px;
    }
    &__logo {
      margin-bottom: 12px;
      & svg {
        width: auto;
        height: 22px;
      }
    }
    &__title {
      margin: 0;
      font-size: 26px;
    }
    &__map-wrap {
      order: 1;
      flex: none;
      min-height: 0;
      height: 55vh;
      height: 55svh;
    }
    &__body {
      order: 2;
      padding: 20px 16px 32px;
      color: #fff;
    }
    &__lead {
      margin-bottom: 12px;
      font-size: 16px;
    }
    &__cities,
    &__not-found {
      margin-bottom: 24px;
    }
    &__city {
      padding: 6px 14px;
    }
    &__button {
      max-width: none;
      height: 52px;
    }
  }
}
</style>

<style lang="scss">
// Пини й контроли MapLibre створюються поза шаблоном, тому стилі не scoped.
// position не задаємо: маркеру MapLibre потрібен його власний absolute.
.radar-pin {
  width: 24px;
  cursor: pointer;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 24px;
    height: 24px;
    box-sizing: border-box;
    background: #fce500;
    border: 3px solid #131313;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6);
  }
  &::after {
    content: "";
    position: absolute;
    top: 8px;
    left: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #131313;
  }
  &--small {
    width: 20px;
    &::before {
      width: 20px;
      height: 20px;
    }
    &::after {
      top: 7px;
      left: 7px;
      width: 6px;
      height: 6px;
    }
  }
}

.radar-pin__label {
  position: absolute;
  top: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  padding: 1px 6px;
  border-radius: 4px;
  color: #fce500;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  text-shadow: 0 0 3px #131313, 0 0 3px #131313;
  pointer-events: none;
}

.radar-pin--small .radar-pin__label {
  font-size: 11px;
}

.radar-pin:hover,
.radar-pin--active {
  z-index: 1;
}

.radar-pin--active .radar-pin__label {
  background: #fce500;
  color: #131313;
  text-shadow: none;
}

.radar-promo {
  .maplibregl-map {
    font-family: inherit;
  }
  .maplibregl-ctrl-group {
    background: #1c1c1c;
    border: 1px solid #313131;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    button + button {
      border-top-color: #313131;
    }
    button:not(:disabled):hover {
      background-color: #313131;
    }
  }
  // Іконки зуму — чорні SVG; замість них жовті «+» та «−».
  .maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon,
  .maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon {
    background-image: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fce500;
    font-size: 20px;
    line-height: 1;
  }
  .maplibregl-ctrl-zoom-in .maplibregl-ctrl-icon::before {
    content: "+";
  }
  .maplibregl-ctrl-zoom-out .maplibregl-ctrl-icon::before {
    content: "−";
  }
  button:disabled .maplibregl-ctrl-icon {
    color: #8a8a8a;
  }
  .maplibregl-ctrl-attrib {
    background: rgba(19, 19, 19, 0.7);
    color: #8a8a8a;
    a {
      color: #d9d9d9;
    }
  }
  .maplibregl-ctrl-attrib.maplibregl-compact {
    background: #1c1c1c;
  }
  .maplibregl-ctrl-attrib-button {
    filter: invert(1);
  }
  .maplibregl-cooperative-gesture-screen {
    background: rgba(19, 19, 19, 0.75);
    color: #fff;
    font-size: 16px;
  }
}
</style>
